// Single-deadline highlight + a "Deadline strip" variant that lists the
// next N deadlines. Both read from `matter.deadlines[]` (already
// pre-computed and color-bucketed by the adapter in api/mock.ts).

import { fmtDays } from "../../../lib/dates";
import { SEP } from "../../chrome/SepList";
import type { Deadline } from "../../../data/types";
import type { WidgetProps } from "./types";
import { WidgetCard } from "./WidgetCard";

// The bucket carries the server's word on a hard clock, so it is read
// before the day count.
function urgencyColor(d: Deadline): string {
  if (d.bucket === "critical" || d.days <= 3) return "var(--red-600)";
  if (d.days <= 7) return "var(--warn-600)";
  if (d.days <= 30) return "var(--ink)";
  return "var(--muted)";
}

// A template may be hand-authored through the API, where `ui` is stored as
// Mixed and validated as a bare record - so a term list arrives in whatever
// shape its author typed.
function matchTerms(declared: unknown): string[] {
  const raw = Array.isArray(declared) ? declared : [declared];
  return raw
    .filter((t): t is string => typeof t === "string" && t.trim() !== "")
    .map((t) => t.trim().toLowerCase());
}

// Nothing stands in for a missing row: the nearest unrelated deadline under a
// heading like "One-Year Filing Bar" is a legal fact the file does not hold.
function findMatching(deadlines: Deadline[], declared: unknown): Deadline | undefined {
  const terms = matchTerms(declared);
  if (terms.length === 0) return undefined;
  // Most urgent first, so the card does not depend on the order it was handed.
  return deadlines
    .filter((d) => terms.some((t) => d.label.toLowerCase().includes(t)))
    .reduce<Deadline | undefined>((best, d) => (!best || d.days < best.days ? d : best), undefined);
}

export function DeadlineWidget({ config, matter, template }: WidgetProps) {
  const deadlines = matter.deadlines ?? [];
  const target = findMatching(deadlines, config.match);
  const title = config.title ?? "Deadline";
  // Three states the manager must be able to tell apart: the template never
  // said which deadline this card is about, the case has no open row of that
  // kind, and a row exists.
  const unwired = matchTerms(config.match).length === 0;

  return (
    <WidgetCard title={title} sub={target ? fmtDays(target.days) : "no deadline"}>
      {!target ? (
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>
          {unwired ? (
            <>
              Template <code>{template?.id ?? "unknown"}</code> does not say which deadline this
              card tracks.
            </>
          ) : (
            "No open deadline of this type."
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink)",
              marginBottom: 4,
            }}
          >
            {target.label}
          </div>
          <div
            style={{
              fontVariantNumeric: "tabular-nums",
              fontSize: 12,
              color: urgencyColor(target),
            }}
          >
            {target.when}
            {SEP}
            {fmtDays(target.days)}
          </div>
        </>
      )}
    </WidgetCard>
  );
}

export function DeadlineStripWidget({ matter }: WidgetProps) {
  const deadlines = (matter.deadlines ?? []).slice(0, 5);
  return (
    <WidgetCard title="Deadlines" sub={`${(matter.deadlines ?? []).length} tracked`}>
      {deadlines.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--muted)" }}>No deadlines tracked yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {deadlines.map((d) => (
            <div
              key={d.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 8px",
                borderRadius: 6,
                background: "var(--bg, var(--line-2))",
                borderLeft: `3px solid ${urgencyColor(d)}`,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>{d.label}</div>
              <div
                style={{
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 11.5,
                  color: urgencyColor(d),
                  whiteSpace: "nowrap",
                }}
              >
                {fmtDays(d.days)}
                {SEP}
                {d.when}
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
